import { inject, Injectable } from '@angular/core';
import { StateContext } from '@ngxs/store';
import { FxTransactionModel, TableStateModel } from '../table.state.model';
import { Pagination } from '@core/models/pagination.model';
import { TableActions } from '../table.actions';
import { TransactionsService } from '@core/services/transactions.service';
import { getDefaultTableState } from '../table.state';
import { NotificationFacade } from '@core/facades/notification.facade';

@Injectable({
  providedIn: 'root',
})
export class TransactionsHandler {
  constructor() {}
  transactionService = inject(TransactionsService);
  notify = inject(NotificationFacade);

  handleWalletTransactionLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.transactionService
      .viewWalletTransactionsList({ ...params })
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'walletTransactions',
              data: response.data?.data,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'walletTransactions',
              error: error.message,
            })
          );
        },
      });
  }

  filterWalletTransactionList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterWalletTransctionsList
  ) {
    {
      return this.transactionService
        .filterWalletTransactionsList(action.payload.filter)
        .subscribe({
          next: (response) => {
            ctx.dispatch(
              new TableActions.LoadTableDataSuccess({
                tableKey: 'walletTransactions',
                data: response.data?.data.list,
                totalCount: response.data?.total_count,
              })
            );
          },
          error: (error) => {
            ctx.dispatch(
              new TableActions.LoadTableDataFailure({
                tableKey: 'walletTransactions',
                error: error.message,
              })
            );
          },
        });
    }
  }

  getProvidusBalance(ctx: StateContext<TableStateModel>) {
    return this.transactionService
      .getProvidusSettlementAccountBalance()
      .subscribe({
        next: (response) => {
          const currentState = ctx.getState();
          const currentWalletTransactions =
            currentState.walletTransactions || getDefaultTableState();
          ctx.patchState({
            walletTransactions: {
              ...currentWalletTransactions,
              providusBalance: response.data?.availableBalance,
            },
          });
        },
        error: (error) => {
          this.notify.error(
            'Failed to fetch Providus Settlement Account Balance'
          );
        },
      });
  }
  getAccessBankBalance(ctx: StateContext<TableStateModel>) {
    return this.transactionService
      .getAccessBankSettlementAccountBalance()
      .subscribe({
        next: (response) => {
          const currentState = ctx.getState();
          const currentWalletTransactions =
            currentState.walletTransactions || getDefaultTableState();
          ctx.patchState({
            walletTransactions: {
              ...currentWalletTransactions,
              accessBankBalance: response.data?.availableBalance,
            },
          });
        },
        // error: (error) => {
        //   this.notify.error(
        //     'Failed to fetch Access Bank Settlement Account Balance'
        //   );
        // },
      });
  }

  getVertoBalance(ctx: StateContext<TableStateModel>) {
    return this.transactionService
      .getVertoSettlementAccountBalance()
      .subscribe({
        next: (response) => {
          const currentState = ctx.getState();
          const currentWalletTransactions =
            currentState.walletTransactions || getDefaultTableState();
          ctx.patchState({
            walletTransactions: {
              ...currentWalletTransactions,
              vertoBalance: response.data?.availableBalance,
            },
          });
        },
        // error: (error) => {
        //   this.notify.error(
        //     'Failed to fetch Verto Settlement Account Balance'
        //   );
        // },
      });
  }

  getPaystackBalance(ctx: StateContext<TableStateModel>) {
    return this.transactionService
      .getPaystackSettlementAccountBalance()
      .subscribe({
        next: (response) => {
          if (response.status === 'success') {
            const currentState = ctx.getState();
            const currentWalletTransactions =
              currentState.walletTransactions || getDefaultTableState();
            const balance = response.data?.balance?.data?.[0]?.balance ?? 0;
            ctx.patchState({
              walletTransactions: {
                ...currentWalletTransactions,
                paystackBalance: balance / 100,
              },
            });
          }
        },
        error: (error) => {
          this.notify.error(
            'Failed to fetch Paystack Settlement Account Balance'
          );
        },
      });
  }

  handleFxTransactionLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    const query = `take=${params.size}&skip=${params.skip}`;
    this.transactionService.getFxTransactionsList(query).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'fxTransactions',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'fxTransactions',
            error: error.message,
          })
        );
      },
    });
  }

  filterFxTransactionList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterFxTransactionsList
  ) {
    return this.transactionService
      .getFxTransactionsList(action.payload.filter)
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'fxTransactions',
              data: response.data?.data?.list ?? response.data?.data,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'fxTransactions',
              error: error.message,
            })
          );
        },
      });
  }

  getFxSummary(ctx: StateContext<TableStateModel>) {
    return this.transactionService.getFxTransactionsMetadata().subscribe({
      next: (response) => {
        const currentState = ctx.getState();
        const currentFx: FxTransactionModel = (currentState.fxTransactions as FxTransactionModel) || { ...getDefaultTableState(), totalUSD: 0, totalNGN: 0, dayTotalUSD: 0, dayTotalNgn: 0 };
        ctx.patchState({
          fxTransactions: {
            ...currentFx,
            totalUSD: response.data?.total_usd_amount,
            totalNGN: response.data?.total_naira_equivalent,
            dayTotalUSD: response.data?.today_usd_amount,
            dayTotalNgn: response.data?.today_naira_equivalent,
          },
        });
      },
      error: () => {
        this.notify.error('Failed to fetch FX summary');
      },
    });
  }

  handleCashTransactionLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.transactionService.getCashTransactionList({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'cashTransactions',
            data: response.data?.data,
            totalCount: response.data?.total_acount,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'cashTransactions',
            error: error.message,
          })
        );
      },
    });
  }

  filterCashTransactionList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterWalletTransctionsList
  ) {
    {
      return this.transactionService
        .filterTransactionList(action.payload.filter)
        .subscribe({
          next: (response) => {
            ctx.dispatch(
              new TableActions.LoadTableDataSuccess({
                tableKey: 'cashTransactions',
                data: response.data?.data,
                totalCount: response.data?.total_acount,
              })
            );
          },
          error: (error) => {
            ctx.dispatch(
              new TableActions.LoadTableDataFailure({
                tableKey: 'cashTransactions',
                error: error.message,
              })
            );
          },
        });
    }
  }

  getInvestmentCashBalance(ctx: StateContext<TableStateModel>) {
    return this.transactionService
      .getGlobalPortfolioBalance()
      .subscribe({
        next: (response) => {
          const currentState = ctx.getState();
          const currentCashTransactions =
            currentState.cashTransactions || getDefaultTableState();
          ctx.patchState({
            cashTransactions: {
              ...currentCashTransactions,
              investmentCash: response.data?.balance,
            },
          });
        },
        error: (error) => {
          this.notify.error(
            'Failed to fetch Providus Settlement Account Balance'
          );
        },
      });
  }
  getWalletCashBalance(ctx: StateContext<TableStateModel>) {
    return this.transactionService
      .getGlobalWalletBalance()
      .subscribe({
        next: (response) => {
          const currentState = ctx.getState();
          const currentCashTransactions =
            currentState.cashTransactions || getDefaultTableState();
          ctx.patchState({
            cashTransactions: {
              ...currentCashTransactions,
              walletCash: response.data?.balance,
            },
          });
        },
        error: (error) => {
          this.notify.error(
            'Failed to fetch Optimus Settlement Account Balance'
          );
        },
      });
  }
}
